"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import Image from "next/image";
import Link from "next/link";
import {
  heroCarouselContainerVariants,
  heroCarouselItemVariants,
  heroCarouselImageVariants,
} from "@/lib/animations";
import { heroItems, appDownloadLinks } from "../data";

export function Hero() {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % heroItems.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const currentItem = heroItems[activeIndex];

  return (
    <section className="hero relative overflow-hidden bg-green-2000 px-5 pb-10 pt-20 md:px-6 md:pb-16 lg:px-10 lg:pb-0 lg:pr-0">
      <div className="hero-bg hero-bg--1" />
      <div className="hero-bg hero-bg--2" />
      <div className="hero-bg hero-bg--3" />
      <div className="hero-bg hero-bg--4" />

      <div className="relative mx-auto flex h-full w-full max-w-8xl flex-col gap-12 lg:max-w-full lg:flex-row">
        <div className="pt-10 text-center lg:flex-1 lg:pt-20 lg:text-left xl:pt-32">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeIndex}
              variants={heroCarouselContainerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <motion.h1
                variants={heroCarouselItemVariants}
                className="text-5xl font-semibold tracking-copy md:text-6xl lg:text-6xl xl:text-[80px] xl:leading-[80px]"
              >
                {currentItem.title}
              </motion.h1>
              <motion.p
                variants={heroCarouselItemVariants}
                className="mb-10 mt-8 text-xl font-medium tracking-copy text-black-400 md:text-2xl lg:text-3xl"
              >
                {currentItem.description}
              </motion.p>
              <motion.div
                variants={heroCarouselItemVariants}
                className="flex items-center justify-center gap-4 lg:justify-start"
              >
                {appDownloadLinks.map((link) => (
                  <Link
                    href={link.link}
                    key={link.id}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Image
                      src={link.imageUrl}
                      alt={link.alt}
                      width={link.width}
                      height={link.height}
                      aria-label={link.alt}
                      unoptimized
                    />
                  </Link>
                ))}
              </motion.div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="relative lg:flex-1">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeIndex}
              variants={heroCarouselImageVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="relative z-10 flex h-full w-full items-center justify-center"
            >
              <Image
                src={currentItem.imageUrl}
                alt={currentItem.imageAlt}
                className="w-full rounded-lg lg:rounded-none"
                width={578}
                height={581}
                priority
              />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
